<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

    <!-- 1. Sidebar -->
    <HrSidebar v-model:isOpen="isSidebarOpen" />

    <!-- 2. Main Content Wrapper -->
    <div class="flex flex-1 flex-col min-w-0 transition-all duration-300 md:ml-64">

      <!-- 3. Topbar -->
      <header class="sticky top-0 z-30 h-16 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700">
        <HrTopbar
          :page-title="currentPageTitle"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <!-- 4. Page Content -->
      <main class="flex-1 p-4 sm:p-6 lg:p-8">

        <!-- Loading State -->
        <div v-if="loading" class="flex flex-col justify-center items-center h-64">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-[#035a76]"></div>
          <p class="mt-4 text-gray-600 dark:text-gray-400 font-medium">Loading employee profile...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 p-6 rounded-xl flex items-start gap-3">
          <svg class="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p class="font-semibold">Failed to load employee profile</p>
            <p class="mt-1 text-sm">{{ error }}</p>
          </div>
        </div>

        <!-- Content -->
        <template v-else-if="employee && employee.personal">

          <!-- Profile Header (always visible) -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div class="flex items-center gap-5">
                <div class="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-gradient-to-br from-[#035a76] to-[#04698f] flex items-center justify-center text-white text-2xl sm:text-3xl font-bold shadow-md flex-shrink-0">
                  {{ getInitials(employee.personal.first_name, employee.personal.last_name) }}
                </div>
                <div>
                  <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                    {{ employee.personal.first_name }} {{ employee.personal.last_name }}
                  </h1>
                  <div class="flex flex-wrap items-center gap-3 mt-2">
                    <span class="text-sm text-gray-500 dark:text-gray-400">
                      ID: <span class="font-mono font-medium text-gray-700 dark:text-gray-300">{{ displayValue(employee.personal.employee_number) }}</span>
                    </span>
                    <span class="hidden sm:inline text-gray-300 dark:text-gray-600">•</span>
                    <span class="text-sm text-gray-500 dark:text-gray-400">
                      {{ employee.personal.position_title || 'Staff Member' }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="flex items-center gap-3 sm:flex-col sm:items-end">
                <span :class="getStatusClass(employee.contract?.status)" class="px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide w-fit">
                  {{ displayValue(employee.contract?.status) || 'No Contract' }}
                </span>
                <button
                  @click="router.push({ name: 'EditEmployeeProfile', params: { id: applicantId } })"
                  class="inline-flex items-center px-4 py-2 bg-[#035a76] hover:bg-[#024c63] text-white text-sm font-medium rounded-lg shadow-sm transition-colors"
                >
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Update Profile
                </button>
              </div>
            </div>
          </div>

          <!-- TAB BAR (sticks under the topbar while scrolling) -->
          <nav class="sticky top-16 z-20 -mx-4 sm:mx-0 mb-6 bg-gray-50/90 dark:bg-gray-900/90 backdrop-blur border-b border-gray-200 dark:border-gray-700">
            <ul class="flex gap-1 overflow-x-auto px-4 sm:px-0 no-scrollbar">
              <li v-for="tab in tabs" :key="tab.name" class="shrink-0">
                <router-link
                  :to="{ name: tab.name, params: { id: applicantId } }"
                  class="inline-flex items-center px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap"
                  :class="route.name === tab.name
                    ? 'border-[#035a76] text-[#035a76] dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'"
                >
                  {{ tab.label }}
                </router-link>
              </li>
            </ul>
          </nav>

          <!-- ACTIVE TAB CONTENT -->
          <router-view v-slot="{ Component }">
            <transition name="fade" mode="out-in">
              <component :is="Component" />
            </transition>
          </router-view>

        </template>

        <!-- No data state -->
        <div v-else class="flex flex-col items-center justify-center py-20 text-gray-500 dark:text-gray-400">
          <p class="text-lg font-medium">No employee data available</p>
        </div>

      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, provide, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import HrSidebar from '../components/hr/HrSidebar.vue'
import HrTopbar from '../components/hr/HrTopbar.vue'
import { displayValue, getInitials, getStatusClass } from '@/composables/useStaffHelpers'

const route = useRoute()
const router = useRouter()
const applicantId = route.params.id

// Route names must match the children in router/index.js
const tabs = [
  { name: 'staffinfo',       label: 'Personal Info' },
  { name: 'StaffLeaves',     label: 'Leaves' },
  { name: 'StaffAttendance', label: 'Attendance' },
]

const isSidebarOpen = ref(false)
const isDarkMode = ref(document.documentElement.classList.contains('dark'))
const currentPageTitle = ref('Staff Details')

const employee = ref({})
const loading = ref(true)
const error = ref(null)

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value }

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  document.documentElement.classList.toggle('dark', isDarkMode.value)
  localStorage.theme = isDarkMode.value ? 'dark' : 'light'
}

const fetchEmployeeProfile = async () => {
  try {
    loading.value = true
    error.value = null
    const response = await api.get(`get_employee_profile.php?id=${applicantId}`)
    if (response.data?.success) {
      employee.value = response.data
    } else {
      error.value = response.data?.message || 'Invalid response from server'
    }
  } catch (err) {
    console.error('Error fetching employee profile:', err)
    error.value = err.message || 'Network error'
  } finally {
    loading.value = false
  }
}

// Share with every tab
provide('employee', employee)
provide('applicantId', applicantId)
provide('refreshEmployee', fetchEmployeeProfile)

onMounted(fetchEmployeeProfile)
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.15s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.no-scrollbar::-webkit-scrollbar { display: none; }
.no-scrollbar { scrollbar-width: none; }
</style>